import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getProductClientById, ProductDetailDto } from "../api/productApi";
import { useCart } from "../contexts/CartProvider";

/* ======================
   HELPERS
====================== */
function formatVnd(v?: number) {
  if (v == null) return "—";
  return v.toLocaleString("vi-VN") + "₫";
}

function apiBase() {
  const raw = process.env.REACT_APP_API_URL || "";
  return raw.replace(/\/+$/, "");
}

/** Fix ảnh: backend trả "/uploads/..." -> prefix API_URL */
function resolveImg(url?: string | null) {
  if (!url) return "/images/placeholder.jpg";
  if (/^https?:\/\//i.test(url)) return url;

  const base = apiBase();
  if (!base) return url;

  // "/uploads/a.webp" => `${base}/uploads/a.webp`
  if (url.startsWith("/")) return `${base}${url}`;

  // "uploads/a.webp" => `${base}/uploads/a.webp`
  return `${base}/${url}`;
}

type Direction = -1 | 1;

/* ======================
   SafeImg
====================== */
type SafeImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src?: string;
};

function SafeImg({ src, ...props }: SafeImgProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  const normalized =
    src && src !== "/images" && src !== "/images/"
      ? src
      : "/images/placeholder.jpg";

  const finalSrc = failed ? "/images/placeholder.jpg" : normalized;

  return (
    <img
      {...props}
      src={finalSrc}
      loading={props.loading ?? "lazy"}
      decoding={props.decoding ?? "async"}
      onError={() => setFailed(true)}
    />
  );
}

/* ======================
   PAGE
====================== */
export default function ProductDetail() {
  // ✅ Đổi param sang id (khuyến nghị route: /product/:id)
  const params = useParams<{ id?: string; slug?: string }>();
  const id = params.id ?? params.slug;

  const [data, setData] = useState<ProductDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // purchase state
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [qty, setQty] = useState(1);

  // gallery state
  const [activeIndex, setActiveIndex] = useState(0);

  // transition state
  const [isTx, setIsTx] = useState(false);
  const [fromIndex, setFromIndex] = useState(0);
  const [toIndex, setToIndex] = useState(0);
  const [dir, setDir] = useState<Direction>(1);

  const touchStartX = useRef<number | null>(null);
  const txTimer = useRef<number | null>(null);

  // cart
  const { addItem, open: openCart } = useCart();
  const [adding, setAdding] = useState(false);

  // modal
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  // preview toggles
  const [hardcorePreview, setHardcorePreview] = useState(false);
  const [fitMode, setFitMode] = useState<"contain" | "cover">("contain");

  // lazy lookbook
  const lookbookRef = useRef<HTMLDivElement | null>(null);
  const [showLookbook, setShowLookbook] = useState(false);

  useEffect(() => {
    const el = lookbookRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShowLookbook(true);
          io.disconnect();
        }
      },
      { rootMargin: "350px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  // ✅ LOAD DATA by id using /api/Products/client/{id}
  useEffect(() => {
    const pid = Number(id);
    if (!pid || Number.isNaN(pid)) {
      setData(null);
      setLoading(false);
      setError("Invalid product id");
      return;
    }

    const ac = new AbortController();

    setLoading(true);
    setError(null);

    getProductClientById(pid, ac.signal)
      .then((res) => {
        setData(res);

        setSelectedColor(null);
        setSelectedSize(null);
        setQty(1);

        setActiveIndex(0);
        setFromIndex(0);
        setToIndex(0);
        setIsTx(false);
      })
      .catch((e: any) => {
        // axios abort => ERR_CANCELED
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

        // hiển thị rõ nếu bị auth
        if (e?.response?.status === 401) {
          setError("Bạn cần đăng nhập để xem sản phẩm này.");
        } else if (e?.response?.status === 403) {
          setError("Bạn không có quyền xem sản phẩm này.");
        } else {
          setError(e?.message || "Failed to load product");
        }

        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      ac.abort();
      if (txTimer.current) window.clearTimeout(txTimer.current);
    };
  }, [id]);

  const images = useMemo(() => data?.images ?? [], [data]);
  const maxIndex = Math.max(0, images.length - 1);

  const goTo = (next: number) => {
    if (images.length === 0) return;

    const clamped = Math.max(0, Math.min(maxIndex, next));
    if (clamped === activeIndex) return;

    const direction: Direction = clamped > activeIndex ? 1 : -1;

    if (txTimer.current) window.clearTimeout(txTimer.current);
    setDir(direction);
    setFromIndex(activeIndex);
    setToIndex(clamped);
    setIsTx(true);

    txTimer.current = window.setTimeout(() => {
      setActiveIndex(clamped);
      setIsTx(false);
    }, 260);
  };

  const nextImg = () => goTo(activeIndex + 1);
  const prevImg = () => goTo(activeIndex - 1);

  // swipe mobile
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartX.current;
    const end = e.changedTouches[0]?.clientX ?? null;
    touchStartX.current = null;
    if (start == null || end == null) return;

    const dx = end - start;
    if (Math.abs(dx) < 50) return;
    if (dx < 0) nextImg();
    else prevImg();
  };

  // available colors with stock
  const colors = useMemo(() => {
    if (!data) return [];
    return Array.from(
      new Set(
        data.variants.filter((v) => v.stockQuantity > 0).map((v) => v.color),
      ),
    );
  }, [data]);

  const sizesByColor = useMemo(() => {
    if (!data || !selectedColor) return [];
    const sizes = data.variants
      .filter((v) => v.color === selectedColor && v.stockQuantity > 0)
      .map((v) => v.size);
    return Array.from(new Set(sizes));
  }, [data, selectedColor]);

  const selectedVariant = useMemo(() => {
    if (!data || !selectedColor || !selectedSize) return undefined;
    return data.variants.find(
      (v) => v.color === selectedColor && v.size === selectedSize,
    );
  }, [data, selectedColor, selectedSize]);

  useEffect(() => {
    if (!showSizeGuide) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowSizeGuide(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showSizeGuide]);

  const lookbookItems = useMemo(() => {
    const fromProduct = (data?.images ?? []).slice(0, 8).map((img, i) => ({
      id: img.id ?? `p-${i}`,
      imageUrl: resolveImg(img.imageUrl),
      href: "#",
    }));

    if (fromProduct.length > 0) return fromProduct;

    return Array.from({ length: 8 }).map((_, i) => ({
      id: `ph-${i}`,
      imageUrl: "/images/placeholder.jpg",
      href: "#",
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="mx-auto max-w-[1200px] 2xl:max-w-[1360px] px-4 sm:px-6">
          <div className="animate-pulse">
            <div className="h-6 w-48 bg-black/10" />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-14">
              <div className="aspect-[4/5] bg-black/5 border border-black/10" />
              <div>
                <div className="h-8 w-2/3 bg-black/10" />
                <div className="mt-4 h-6 w-40 bg-black/10" />
                <div className="mt-10 h-10 w-full bg-black/10" />
                <div className="mt-4 h-10 w-full bg-black/10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="mx-auto max-w-[1200px] 2xl:max-w-[1360px] px-4 sm:px-6">
          <div className="border border-red-500/20 bg-red-500/5 p-6">
            <p className="text-[10px] uppercase tracking-[0.55em] text-red-700/80">
              Product load error
            </p>
            <p className="mt-3 text-sm text-red-700/80">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const objectFitClass =
    fitMode === "contain" ? "object-contain" : "object-cover";

  const price = selectedVariant?.price ?? data.salePrice ?? data.basePrice;
  const stock = selectedVariant?.stockQuantity;

  const currentUrl = resolveImg(images[activeIndex]?.imageUrl);
  const fromUrl = resolveImg(images[fromIndex]?.imageUrl);
  const toUrl = resolveImg(images[toIndex]?.imageUrl);

  const canSelect = Boolean(selectedVariant);

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    try {
      setAdding(true);
      await addItem({
        productId: data.id, // hoặc productId của backend
        color: selectedColor!,
        size: selectedSize!,
        quantity: qty,
      });

      openCart();
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-white text-black pt-24 pb-28 sm:pb-40 lg:pb-56">
        <div className="mx-auto max-w-[1200px] 2xl:max-w-[1360px] px-4 sm:px-6">
          {/* Top micro line */}
          <div className="mb-6 sm:mb-8 flex items-center justify-between gap-4">
            <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
              OWL DREAM — Product
            </p>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setHardcorePreview((v) => !v)}
                className="text-[10px] uppercase tracking-[0.55em] border border-black/15 px-3 py-1 hover:border-black/40 transition"
              >
                {hardcorePreview ? "Normal" : "Hardcore"}
              </button>

              <button
                type="button"
                onClick={() =>
                  setFitMode((m) => (m === "contain" ? "cover" : "contain"))
                }
                className="text-[10px] uppercase tracking-[0.55em] border border-black/15 px-3 py-1 hover:border-black/40 transition"
                title="Contain: không cắt ảnh | Cover: full khung"
              >
                {fitMode === "contain" ? "CONTAIN" : "COVER"}
              </button>

              {images.length > 1 && (
                <p className="text-[10px] uppercase tracking-[0.55em] text-black/35">
                  {activeIndex + 1}/{images.length}
                </p>
              )}
            </div>
          </div>

          {/* ✅ Layout mới: gallery to hơn chút trên desktop, info sticky */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] gap-8 sm:gap-10 xl:gap-14 items-start">
            {/* ================= GALLERY ================= */}
            <div className="relative">
              <div
                className="relative overflow-hidden border border-black/10 bg-black/[0.02]"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                <div className="relative h-[64vh] min-h-[420px] max-h-[780px] sm:h-[68vh] lg:h-[74vh] lg:min-h-[560px] lg:max-h-[860px]">
                  {!isTx && (
                    <SafeImg
                      src={currentUrl}
                      alt={data.name}
                      className={`absolute inset-0 h-full w-full ${objectFitClass}`}
                      draggable={false}
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.04))",
                      }}
                    />
                  )}

                  {isTx && (
                    <>
                      <SafeImg
                        src={fromUrl}
                        alt=""
                        className={`absolute inset-0 h-full w-full ${objectFitClass} transition-all duration-[260ms]`}
                        style={{
                          opacity: 0,
                          transform: `translateX(${dir * -10}px) scale(1.01)`,
                        }}
                        draggable={false}
                      />
                      <SafeImg
                        src={toUrl}
                        alt={data.name}
                        className={`absolute inset-0 h-full w-full ${objectFitClass} transition-all duration-[260ms]`}
                        style={{
                          opacity: 1,
                          transform: `translateX(0px) scale(1)`,
                        }}
                        draggable={false}
                      />
                    </>
                  )}

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/[0.08]" />
                  {hardcorePreview && (
                    <div className="pointer-events-none absolute inset-0 mix-blend-multiply bg-black/20" />
                  )}
                </div>
              </div>

              {/* Controls */}
              {images.length > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={prevImg}
                    disabled={activeIndex === 0}
                    className="text-[10px] uppercase tracking-[0.5em] text-black/55 hover:text-black transition disabled:opacity-30"
                  >
                    ← Prev
                  </button>

                  <button
                    type="button"
                    onClick={nextImg}
                    disabled={activeIndex === maxIndex}
                    className="text-[10px] uppercase tracking-[0.5em] text-black/55 hover:text-black transition disabled:opacity-30"
                  >
                    Next →
                  </button>
                </div>
              )}

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="mt-6 grid grid-cols-6 sm:grid-cols-8 gap-3">
                  {images.slice(0, 8).map((img, i) => {
                    const url = resolveImg(img.imageUrl);
                    const active = i === activeIndex;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => goTo(i)}
                        className={`relative overflow-hidden border transition ${
                          active
                            ? "border-black"
                            : "border-black/10 hover:border-black/40"
                        }`}
                      >
                        <div className="aspect-[4/5] bg-black/[0.02]">
                          <SafeImg
                            src={url}
                            alt=""
                            className={`h-full w-full ${objectFitClass} transition ${
                              active
                                ? "opacity-100"
                                : "opacity-70 hover:opacity-90"
                            }`}
                            draggable={false}
                          />
                        </div>
                        {active && (
                          <div className="pointer-events-none absolute inset-0 ring-1 ring-black/20" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {images.length > 1 && (
                <p className="mt-4 text-[10px] uppercase tracking-[0.55em] text-black/35 lg:hidden">
                  Swipe to view gallery
                </p>
              )}
            </div>

            {/* ================= INFO / BUY ================= */}
            <div className="lg:sticky lg:top-28">
              <div className="border border-black/10 bg-white">
                <div className="p-6 sm:p-8 md:p-10">
                  <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                    OWL DREAM
                  </p>

                  <h1 className="mt-5 text-[24px] sm:text-[28px] md:text-[34px] font-medium uppercase tracking-[0.18em] leading-tight">
                    {data.name}
                  </h1>

                  <div className="mt-6 flex items-end justify-between gap-6">
                    <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                      Price
                    </p>
                    <div className="text-right">
                      {data.salePrice != null && (
                        <p className="text-xs text-black/40 line-through">
                          {formatVnd(data.basePrice)}
                        </p>
                      )}
                      <p className="text-[18px] font-light tracking-wide">
                        {formatVnd(price)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 h-px bg-black/10" />

                  {/* COLOR */}
                  <div className="mt-8">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                        Color
                      </p>
                      {selectedColor && (
                        <p className="text-[10px] uppercase tracking-[0.55em] text-black/35">
                          {selectedColor}
                        </p>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {colors.map((c) => {
                        const active = c === selectedColor;
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => {
                              setSelectedColor(c);
                              setSelectedSize(null);
                              setQty(1);
                            }}
                            className={`px-4 py-2 border text-[11px] uppercase tracking-[0.35em] transition ${
                              active
                                ? "border-black bg-black text-white"
                                : "border-black/15 hover:border-black/50"
                            }`}
                          >
                            {c}
                          </button>
                        );
                      })}
                      {colors.length === 0 && (
                        <p className="text-sm text-black/50">
                          Hết hàng (không có variant còn stock).
                        </p>
                      )}
                    </div>
                  </div>

                  {/* SIZE */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                        Size
                      </p>

                      <button
                        type="button"
                        onClick={() => setShowSizeGuide(true)}
                        className="text-[10px] uppercase tracking-[0.45em] text-black/45 hover:text-black underline underline-offset-4"
                      >
                        Size guide
                      </button>

                      {selectedSize && (
                        <p className="text-[10px] uppercase tracking-[0.55em] text-black/35">
                          {selectedSize}
                        </p>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {!selectedColor ? (
                        <p className="text-xs text-black/50">
                          Chọn màu trước để hiện size.
                        </p>
                      ) : sizesByColor.length === 0 ? (
                        <p className="text-xs text-black/50">
                          Màu này hết size còn hàng.
                        </p>
                      ) : (
                        sizesByColor.map((s) => {
                          const active = s === selectedSize;
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setSelectedSize(s)}
                              className={`h-10 min-w-12 px-4 border text-[11px] uppercase tracking-[0.35em] transition ${
                                active
                                  ? "border-black bg-black text-white"
                                  : "border-black/15 hover:border-black/50"
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* QTY */}
                  <div className="mt-6">
                    <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                      Quantity
                    </p>
                    <div className="mt-3 inline-flex items-center border border-black/15">
                      <button
                        type="button"
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        className="h-10 w-12 text-black/70 hover:text-black transition"
                      >
                        −
                      </button>
                      <div className="h-10 w-12 grid place-items-center text-sm">
                        {qty}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setQty((q) =>
                            typeof stock === "number" && stock > 0
                              ? Math.min(stock, q + 1)
                              : q + 1,
                          )
                        }
                        className="h-10 w-12 text-black/70 hover:text-black transition"
                      >
                        +
                      </button>
                    </div>

                    {typeof stock === "number" && (
                      <p className="mt-2 text-xs text-black/45">
                        Stock: {stock}
                      </p>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="mt-8 space-y-3">
                    <button
                      type="button"
                      disabled={!canSelect || adding}
                      className="w-full h-12 bg-black text-white text-[11px] uppercase tracking-[0.55em] transition disabled:opacity-35"
                      onClick={handleAddToCart}
                    >
                      {adding ? "Adding..." : "Add to cart"}
                    </button>

                    <button
                      type="button"
                      disabled={!canSelect}
                      className="w-full h-12 border border-black text-black text-[11px] uppercase tracking-[0.55em] hover:bg-black hover:text-white transition disabled:opacity-35"
                      onClick={() => {
                        // TODO: Buy now flow (checkout)
                      }}
                    >
                      Buy now
                    </button>

                    {!canSelect && (
                      <p className="text-xs text-black/45">
                        Vui lòng chọn màu + size để mua.
                      </p>
                    )}
                  </div>

                  <div className="mt-8 h-px bg-black/10" />

                  {/* DESCRIPTION */}
                  <p className="mt-8 text-sm leading-relaxed text-black/60">
                    {data.description ||
                      "Designed for night thinkers. Heavyweight cotton, relaxed silhouette, limited drop — built to wear hard and look clean."}
                  </p>

                  {/* DETAILS */}
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-[11px] tracking-wide text-black/55">
                    <div className="border border-black/10 p-4">
                      <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                        Fabric
                      </p>
                      <p className="mt-2">Heavyweight cotton</p>
                    </div>
                    <div className="border border-black/10 p-4">
                      <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                        Fit
                      </p>
                      <p className="mt-2">Relaxed / Oversize</p>
                    </div>
                    <div className="border border-black/10 p-4">
                      <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                        Drop
                      </p>
                      <p className="mt-2">Limited production</p>
                    </div>
                    <div className="border border-black/10 p-4">
                      <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                        Care
                      </p>
                      <p className="mt-2">Cold wash / low heat</p>
                    </div>
                  </div>

                  <div className="mt-10 flex items-center gap-4">
                    <div className="h-px flex-1 bg-black/10" />
                    <p className="text-[10px] uppercase tracking-[0.55em] text-black/35">
                      Born from the night
                    </p>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-[10px] uppercase tracking-[0.55em] text-black/35 hidden lg:block">
                Tip: choose color & size for accurate price + stock.
              </p>
            </div>
          </div>

          {/* Instagram / Lookbook (lazy mount) */}
          <div ref={lookbookRef} className="mt-20 sm:mt-24">
            <div className="flex items-center gap-6">
              <div className="h-px flex-1 bg-black/10" />
              <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                Instagram / Lookbook
              </p>
              <div className="h-px flex-1 bg-black/10" />
            </div>

            {!showLookbook ? (
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square border border-black/10 bg-black/[0.03] animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                {lookbookItems.map((it) => (
                  <a
                    key={it.id}
                    href={it.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative overflow-hidden border border-black/10 bg-black/[0.02]"
                  >
                    <div className="aspect-square">
                      <SafeImg
                        src={it.imageUrl}
                        alt=""
                        className="h-full w-full object-cover opacity-90 group-hover:opacity-100 transition"
                        draggable={false}
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-0 ring-0 group-hover:ring-1 group-hover:ring-black/25 transition" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowSizeGuide(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 mx-auto w-[92vw] max-w-lg bg-white border border-black/15"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.55em] text-black/45">
                    OWL DREAM
                  </p>
                  <h3 className="mt-2 text-lg uppercase tracking-[0.25em]">
                    Size Guide
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSizeGuide(false)}
                  className="h-9 w-9 border border-black/15 hover:border-black/40 transition"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="mt-6 overflow-hidden border border-black/10">
                <table className="w-full text-sm">
                  <thead className="bg-black/[0.02]">
                    <tr>
                      <th className="text-left p-3 text-[10px] uppercase tracking-[0.45em] text-black/45">
                        Size
                      </th>
                      <th className="text-left p-3 text-[10px] uppercase tracking-[0.45em] text-black/45">
                        Weight
                      </th>
                      <th className="text-left p-3 text-[10px] uppercase tracking-[0.45em] text-black/45">
                        Height
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-black/10">
                      <td className="p-3">M</td>
                      <td className="p-3">60–72kg</td>
                      <td className="p-3">160–172cm</td>
                    </tr>
                    <tr className="border-t border-black/10">
                      <td className="p-3">L</td>
                      <td className="p-3">72–80kg</td>
                      <td className="p-3">168–178cm</td>
                    </tr>
                    <tr className="border-t border-black/10">
                      <td className="p-3">XL</td>
                      <td className="p-3">80–92kg</td>
                      <td className="p-3">175–185cm</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <p className="text-xs text-black/45">
                  Tip: đo ngang ngực / dài áo để chuẩn nhất.
                </p>
                <button
                  type="button"
                  onClick={() => setShowSizeGuide(false)}
                  className="h-11 px-8 bg-black text-white text-[11px] uppercase tracking-[0.55em] hover:opacity-90 transition"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
